import { useContext, useEffect, useState, useCallback } from "react";
import { QuestionProps } from "./Props";
import { useToast, Select, SelectOptionType } from "@aidkitorg/component-library";
import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent } from "../Util";
import { generateAccessibleLabel } from "../utils/generateAccessibleLabel";

export function SearchSelectQuestion(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const targetField = props["Target Field"]!;

  const [allOptions, setAllOptions] = useState<SelectOptionType[]>([]);
  const [selectedOption, setSelectedOption] = useState<SelectOptionType | undefined>();

  const { search_type = {}, max_options } = JSON.parse(props['Metadata'] || '{}');
  const { optionsFileUrl = {} } = search_type;

  const handleSelectOption = useCallback((option: SelectOptionType | undefined) => {
    props.setInfoKey(targetField, option?.value || '', !!option, false);
    setSelectedOption(option);
  }, [props.setInfoKey, targetField]);

  // Fetch options on mount and when language changes
  useEffect(() => {
    if (!optionsFileUrl) return;

    const fetchData = async () => {
      try {
        // if no options file is specified for the user's language, use the English one
        const url = optionsFileUrl[context.lang] || optionsFileUrl['en'];
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        const optionsInOldFormat: { id: string; text: string }[] = await res.json();

        if (!Array.isArray(optionsInOldFormat)) {
          console.log('Found options that are not an array.');
          toast({
            description: L.questions.search_select.options_error,
            variant: 'error'
          });
          return;
        }

        const options = optionsInOldFormat.map(({ id, text }) => ({ label: text, value: id }));
        setAllOptions(options);
        const optionId = props.info[targetField];
        const option = options?.find(o => o.value === optionId);
        setSelectedOption(option);
      } catch (err) {
        console.log(err);
        toast({
          description: L.questions.search_select.options_error,
          variant: 'error'
        });
      }
    };

    fetchData();
  }, [context.lang]);

  const content = props[languageContent(context.lang)] || '';
  const formattedContent = useModularMarkdown({
    content,
    info: props.info
  });

  const accessibleLabel = generateAccessibleLabel(props[languageContent(context.lang)])

  if ((props['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  return (
    <Select
      options={allOptions}
      value={selectedOption}
      onChange={handleSelectOption}
      disabled={props.blockQuestionEdits}
      description={accessibleLabel}
      label={formattedContent}
      virtualized={typeof max_options === 'number' && max_options > 80}
      searchable
      portal
      clearable
    />
  );
}
